<template>
  <component
    v-focusable="{ attachTabIndex: false }"
    :tabindex="iconTabindex"
    :is="wrapperComponent"
    :to="to"
    :open-in-new-tab="openInNewTab"
    :title="hint"
    :role="roleAttributeValue"
    class="a-icon"
    :class="iconClasses"
    v-bind="wrapperAttrs"
    @click="onClick"
    @focus="onFocus"
    @blur="onBlur"
  >
    <svgicon
      v-if="isIconLoaded"
      :icon="icon"
      class="a-icon__icon"
      :style="iconStyle"
      :original="original"
    />
    <div v-else :style="iconStyle" />
    <template v-if="$helper.isNotAmpPage($route.name)">
      <span
        v-if="titleOnHover || title"
        class="a-icon__description text-tiny-legacy"
        :style="titleDynamicStyle"
        :title="title"
        :title-on-hover="titleOnHover || title"
        :width="titleWidth"
      />
    </template>
    <span
      v-else-if="titleOnHover || title"
      :style="titleDynamicStyle"
      class="a-icon__description amp text-tiny-legacy"
      >{{ titleOnHover || title }}</span
    >
    <slot name="additional-info" />
  </component>
</template>

<script>
import { propValidator, PROP_TYPES } from '@/utils/validators'

import '@/assets/svg/icons-compiled/magnifier'
import '@/assets/svg/icons-compiled/magnifier-3'
import '@/assets/svg/icons-compiled/white-arrow-right'
import '@/assets/svg/icons-compiled/bell-white'
import '@/assets/svg/icons-compiled/one-signal-active'
import '@/assets/svg/icons-compiled/one-signal-inactive'
import '@/assets/svg/icons-compiled/newsletter-rect-blue'
import '@/assets/svg/icons-compiled/envelope-rect-blue'
import '@/assets/svg/icons-compiled/il'
import '@/assets/svg/icons-compiled/envelope-with-content'
import '@/assets/svg/icons-compiled/warning-icon'
import '@/assets/svg/icons-compiled/live-feed'
import '@/assets/svg/icons-compiled/arrow-down-purple'
import '@/assets/svg/icons-compiled/burger-menu'
import '@/assets/svg/icons-compiled/flash-icon'
import '@/assets/svg/icons-compiled/plus-circle-icon'

import { ICON } from './enums'
export { ICON }

/**
 * When modifying this list make sure you remove/add a relevant import above!
 */
const ICONS_TO_EXCLUDE_FROM_LAZY_LOAD = [
  ICON.MAGNIFIER,
  ICON.MAGNIFIER_3,
  ICON.ARROW_RIGHT_WHITE,
  ICON.NEWSLETTER_RECT_BLUE,
  ICON.ENVELOPE_RECT_BLUE,
  ICON.IL,
  ICON.ENVELOPE_WITH_CONTENT,
  ICON.LIVE_FEED,
  ICON.ARROW_DOWN_PURPLE,
  ICON.BURGER_MENU
]

const ICON_VALUES = Object.values(ICON)

export const ICON_COLOR = {
  BLUE: 'Blue',
  WHITE: 'White',
  BLACK: 'Black'
}

export const ICON_COLOR_VALUES = Object.values(ICON_COLOR)

const ICON_CLASS = {
  [ICON_COLOR.BLACK]: 'a-icon_black',
  [ICON_COLOR.WHITE]: 'a-icon_white',
  [ICON_COLOR.BLUE]: 'a-icon_blue'
}

const WRAPPER_COMPONENT = {
  LINK: 'a-link',
  DIV: 'div'
}

const DEFAULT_TITLE_WIDTH = 115

export default {
  name: 'AIcon',
  inheritAttrs: false,
  props: {
    icon: propValidator([PROP_TYPES.STRING], true, null, icon =>
      ICON_VALUES.includes(icon)
    ),
    title: propValidator([PROP_TYPES.STRING], false, ''),
    titleOnHover: propValidator([PROP_TYPES.STRING], false, ''),
    titleStatic: propValidator([PROP_TYPES.BOOLEAN], false, false),
    titleWidth: propValidator([PROP_TYPES.NUMBER], false, DEFAULT_TITLE_WIDTH),
    hint: propValidator([PROP_TYPES.STRING], false, ''),
    to: propValidator([PROP_TYPES.STRING, PROP_TYPES.OBJECT], false),
    width: propValidator([PROP_TYPES.NUMBER, PROP_TYPES.STRING]),
    height: propValidator([PROP_TYPES.NUMBER, PROP_TYPES.STRING]),
    color: propValidator([PROP_TYPES.STRING], false, null, prop =>
      ICON_COLOR_VALUES.includes(prop)
    ),
    /**
     * This prop turns off iconColor props because original colors are used
     * Can be used for colored icons but there is no possibility to change
     * icon colors on hover
     */
    original: propValidator([PROP_TYPES.BOOLEAN], false, false),
    disabled: propValidator([PROP_TYPES.BOOLEAN], false, false),
    isButton: propValidator([PROP_TYPES.BOOLEAN], false, false),
    noHover: propValidator([PROP_TYPES.BOOLEAN], false, false),
    openInNewTab: propValidator([PROP_TYPES.ANY], false, undefined),
    fill: propValidator([PROP_TYPES.STRING], false),
    id: propValidator([PROP_TYPES.STRING], false, ''),
    ariaLabel: propValidator([PROP_TYPES.STRING], false, '')
  },
  data() {
    return {
      isIconLoaded: false
    }
  },
  computed: {
    iconTabindex() {
      const { tabindex } = this.$attrs

      if (tabindex) return tabindex

      return this.disabled || (!this.isButton && !this.to) ? -1 : 0
    },
    roleAttributeValue() {
      return this.isButton ? 'button' : 'none'
    },
    wrapperComponent() {
      if (this.isButton) {
        return WRAPPER_COMPONENT.DIV
      }

      if (this.to && !this.disabled) {
        return WRAPPER_COMPONENT.LINK
      }

      return WRAPPER_COMPONENT.DIV
    },
    iconWidthStyleValue() {
      return this.$helper.isString(this.width) ? this.width : `${this.width}px`
    },
    iconHeightStyleValue() {
      return this.$helper.isString(this.height)
        ? this.height
        : `${this.height}px`
    },
    iconMinHeightStyle() {
      return this.$helper.isString(this.height)
        ? {}
        : { minHeight: `${this.height}px` }
    },
    iconSizeStyles() {
      return {
        ...(this.width ? { width: this.iconWidthStyleValue } : {}),
        ...(this.height
          ? {
              height: this.iconHeightStyleValue,
              ...this.iconMinHeightStyle
            }
          : {}),
        ...(this.height
          ? { height: `${this.height}px`, minHeight: `${this.height}px` }
          : {})
      }
    },
    iconStyle() {
      return {
        ...this.iconSizeStyles,
        ...(this.fill ? { fill: this.fill } : {})
      }
    },
    iconClasses() {
      return [
        ICON_CLASS[this.color],
        {
          'a-icon_static-title': this.titleStatic,
          'a-icon_original': this.original,
          'a-icon_disabled': this.disabled,
          'a-icon_div': this.wrapperComponent === WRAPPER_COMPONENT.DIV,
          'a-icon_no-hover': this.noHover,
          'a-icon_is-button': this.isButton
        }
      ]
    },
    titleDynamicStyle() {
      return {
        width: `${this.titleWidth}px`
        // maxWidth: `${this.titleWidth}px`
      }
    },
    wrapperAttrs() {
      return {
        ...this.$attrs,
        ...(this.id ? { id: this.id } : {}),
        ...(this.ariaLabel ? { 'aria-label': this.ariaLabel } : {})
      }
    }
  },
  watch: {
    icon: {
      async handler() {
        this.isIconLoaded = false

        if (ICONS_TO_EXCLUDE_FROM_LAZY_LOAD.includes(this.icon)) {
          this.isIconLoaded = true
          return
        }

        await import(`@/assets/svg/icons-compiled/${this.icon}`)
        this.isIconLoaded = true
      },
      immediate: true
    }
  },
  methods: {
    onClick(event) {
      if (this.disabled) {
        return
      }

      this.$emit('click', event)
    },
    onBlur(event) {
      this.$emit('blur', event)
    },
    onFocus(event) {
      this.$emit('focus', event)
    }
  },
  created() {
    if (process.server && this.$helper.isAmpPage(this.$route.name)) {
      this.isIconLoaded = true

      Object.values(ICON).forEach(icon => {
        require(`@/assets/svg/icons-compiled/${icon}`)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.a-icon {
  width: fit-content;
  height: fit-content;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  .a-icon__description {
    position: absolute;
    width: 100%;
    margin-top: 2px;
    text-align: center;
    top: 34px;
    font-size: 12px;
    line-height: 16px;
    white-space: normal;
    color: rgba($c--gray-2, 0.5);
    transition: opacity 0.1s;
    user-select: none;
    content: '';

    &::before {
      display: inline-block;
      width: 100%;
      max-width: 200px; /* maximum title width */
      content: attr(title);
      text-align: center;
    }

    &.amp {
      width: 50px;
    }

    &.amp::before {
      display: none;
    }
  }

  &.a-icon_disabled {
    opacity: 0.4;
    cursor: not-allowed;

    &.a-icon_is-button {
      cursor: not-allowed;
    }
  }

  &.a-icon_is-button {
    cursor: pointer;
  }

  .a-icon__icon {
    width: inherit;
    height: inherit;
    transition: width 0.5s, height 0.5s;
  }

  .a-icon_black .a-icon__icon {
    fill: $c--black;
  }

  &.a-icon_white {
    .a-icon__icon {
      fill: $c--white;
    }

    .a-icon__description {
      color: $c--white;
    }
  }

  &.a-icon_blue .a-icon__icon {
    fill: $c--main;
  }

  &.a-icon_no-hover {
    color: $c--black;
  }

  &.a-icon_static-title {
    .a-icon__description {
      position: static;
    }
  }
}

.a-icon.a-icon_no-hover:hover {
  color: $c--black;
}

.a-icon:not(.a-icon_no-hover):not(.a-icon_disabled):hover {
  .a-icon__description::before {
    content: attr(title-on-hover);
  }

  .a-icon_original {
    .a-icon__icon {
      opacity: 0.7;
    }
  }

  &.a-icon_blue,
  &.a-icon_white {
    .a-icon__icon {
      fill: $c--black;
    }

    .a-icon__description {
      color: $c--black;
    }
  }

  &.a-icon_black {
    .a-icon__icon {
      fill: $c--main;
    }

    .a-icon__description {
      color: $c--main;
    }
  }
}
</style>
